import { useState } from 'react';
import { Link } from 'react-router-dom';
import { flavorSpots } from '@/mocks/flavors';

export default function FlavorMap() {
  const [activeId, setActiveId] = useState(flavorSpots[4].id);
  const active = flavorSpots.find((spot) => spot.id === activeId) ?? flavorSpots[0];

  return (
    <section id="map" className="bg-background-50 py-16 md:py-28">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="reveal mb-12 md:mb-16">
          <div className="mb-6 flex items-center gap-3">
            <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
              Section 05
            </span>
            <span className="h-px w-8 bg-primary-300"></span>
            <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
              A Taste Map of China
            </span>
          </div>
          <h2 className="font-heading text-3xl leading-snug text-foreground-950 md:text-5xl">
            中国味道地图
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            点一点地图上的城市，看看那里的一道味道、一方风土、一个愿望，最后长成了哪一个 NOMO 灵。
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="reveal lg:col-span-7">
            <div className="relative h-[420px] w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-100 md:h-[540px]">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20rice%20paper%20texture%20with%20soft%20ink%20wash%20gradients%20warm%20beige%20cinnabar%20red%20and%20celadon%20green%20tones%20subtle%20grain%20minimal%20eastern%20aesthetic%20no%20text&width=1200&height=900&seq=nomo-map-texture-01&orientation=landscape"
                alt="NOMOISM 中国味道地图抽象底纹"
                title="NOMOISM 中国味道地图"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-background-50/35"></div>

              {flavorSpots.map((spot) => {
                const isActive = spot.id === activeId;
                return (
                  <button
                    key={spot.id}
                    type="button"
                    onClick={() => setActiveId(spot.id)}
                    style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
                    className="group absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    aria-label={`查看 ${spot.city} 的味道地图`}
                  >
                    <span className="flex flex-col items-center">
                      <span
                        className={[
                          'flex items-center justify-center rounded-full transition-all duration-300',
                          isActive
                            ? 'h-5 w-5 bg-primary-500 ring-4 ring-primary-500/25'
                            : 'h-3.5 w-3.5 bg-foreground-700/70 group-hover:bg-primary-400',
                        ].join(' ')}
                      >
                        {isActive && <span className="h-1.5 w-1.5 rounded-full bg-background-50"></span>}
                      </span>
                      <span
                        className={[
                          'mt-2 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] transition-colors duration-300',
                          isActive
                            ? 'bg-foreground-950 text-background-50'
                            : 'bg-background-50/85 text-foreground-700 group-hover:text-primary-600',
                        ].join(' ')}
                      >
                        {spot.city}
                      </span>
                    </span>
                  </button>
                );
              })}

              <span className="absolute bottom-4 left-4 text-[10px] tracking-[0.24em] text-foreground-500 uppercase">
                NOMOISM Flavor Map · China
              </span>
            </div>
          </div>

          <div className="reveal lg:col-span-5" style={{ transitionDelay: '140ms' }}>
            <div className="flex h-full flex-col rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
              <p className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                {active.cityEn}
              </p>
              <h3 className="mt-2 font-heading text-3xl text-foreground-950">{active.city}</h3>

              <div className="mt-7 space-y-5">
                <div className="flex gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className="ri-restaurant-2-line"></i>
                  </span>
                  <div>
                    <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                      一味 · Flavor
                    </p>
                    <p className="mt-1 text-sm text-foreground-900">{active.dish}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                    <i className="ri-landscape-line"></i>
                  </span>
                  <div>
                    <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                      一境 · Place
                    </p>
                    <p className="mt-1 text-sm text-foreground-900">{active.place}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className="ri-heart-3-line"></i>
                  </span>
                  <div>
                    <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                      一愿 · Wish
                    </p>
                    <p className="mt-1 text-sm text-foreground-900">{active.wish}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center justify-between rounded-md bg-background-50 px-5 py-4">
                <div>
                  <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                    NOMO 灵
                  </p>
                  <p className="mt-1 font-heading text-lg text-foreground-950">{active.character}</p>
                </div>
                <span className="text-accent-600">
                  <i className="ri-sparkling-2-line text-xl"></i>
                </span>
              </div>

              <Link
                to={`/flavor/${active.id}`}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                阅读这道味道
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>

        <div className="reveal mt-6 flex flex-wrap gap-2">
          {flavorSpots.map((spot) => (
            <button
              key={spot.id}
              type="button"
              onClick={() => setActiveId(spot.id)}
              className={[
                'rounded-full px-4 py-2 text-xs transition-colors duration-300 cursor-pointer whitespace-nowrap',
                spot.id === activeId
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700',
              ].join(' ')}
            >
              {spot.city} · {spot.dish}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}